import { Injectable, signal, computed } from '@angular/core';

export type UploadStatus = 'uploading' | 'uploaded' | 'failed';

export interface UploadEntry {
  uid: string;
  fileName: string;
  sessionId: string;
  fileSize?: number;
  fileType?: string;
  progress: number; // 0–100
  status: UploadStatus;
}

/**
 * service that tracks per-document S3 upload progress.
 * The session store writes to it; the session component reads from it.
 */
@Injectable({ providedIn: 'root' })
export class UploadProgressService {
  private readonly _uploads = signal<Map<string, UploadEntry>>(new Map());

  readonly uploads = computed(() => this._uploads());

  register(entry: UploadEntry): void {
    this._mutate((map) => map.set(this._key(entry.sessionId, entry.uid), { ...entry }));
  }

  updateProgress(sessionId: string, uid: string, progress: number): void {
    this._mutate((map) => {
      const key = this._key(sessionId, uid);
      const existing = map.get(key);
      if (existing) map.set(key, { ...existing, progress });
    });
  }

  markUploaded(sessionId: string, uid: string): void {
    this._mutate((map) => {
      const key = this._key(sessionId, uid);
      const existing = map.get(key);
      if (existing) map.set(key, { ...existing, progress: 100, status: 'uploaded' });
    });
  }

  markFailed(sessionId: string, uid: string): void {
    this._mutate((map) => {
      const key = this._key(sessionId, uid);
      const existing = map.get(key);
      if (existing) map.set(key, { ...existing, status: 'failed' });
    });
  }

  clearSession(sessionId: string): void {
    this._mutate((map) => {
      for (const key of Array.from(map.keys())) {
        if (key.startsWith(sessionId + ':')) map.delete(key);
      }
    });
  }


  getEntriesForSession(sessionId: string): UploadEntry[] {
    const prefix = sessionId + ':';
    return Array.from(this._uploads().values()).filter((e) =>
      this._key(e.sessionId, e.uid).startsWith(prefix),
    );
  }


  private _key(sessionId: string, uid: string): string {
    return `${sessionId}:${uid}`;
  }

  private _mutate(fn: (map: Map<string, UploadEntry>) => void): void {
    const copy = new Map(this._uploads());
    fn(copy);
    this._uploads.set(copy);
  }
}
